import React, { useCallback } from 'react';
import DynamicCrudPage from '../../../components/DynamicCrudPage';
import {
  useOffers,
  useCompanies,
  useFirms,
  useContractSubjects,
  useScopeOfWork,
  useProposalStatuses,
  useSectors,
  useOffersPermissions,
  SCREENS,
} from '../../../hooks';
import AddIcon from '@mui/icons-material/Add';
import { parseDateForForm } from '../../../utils/dateUtils';

const OffersPage = () => {
  const offersPermissions = useOffersPermissions();

  const customFormFields = useCallback((fields, additionalData, isRootUser) => {
    const {
      companies,
      firms,
      contractSubjects,
      scopeOfWork,
      proposalStatuses,
      sectors,
    } = additionalData;
    const companiesList = companies?.companies || [];
    // Teklif formunda tüm şirket firmaları gösterilmeli (erişim kısıtı yok)
    const firmsList = firms?.referenceFirms || firms?.firms || [];
    const contractSubjectsList = contractSubjects?.contractSubjects || [];
    const scopeOfWorkList = scopeOfWork?.scopeOfWork || [];
    const proposalStatusesList = proposalStatuses?.proposal_status || [];
    const sectorsList = sectors?.sectors || [];
    

    return fields
      .map(field => {
        if (field.name === 'companyId') {
          return {
            ...field,
            options: companiesList.map(c => ({ value: c.id, label: c.name })),
            showIf: () => isRootUser,
          };
        }
        if (field.name === 'firmId') {
          return {
            ...field,
            options: firmsList.map(f => ({ value: f.id, label: f.firm_name })),
          };
        }
        if (field.name === 'firm_short_name') {
          return {
            ...field,
            type: 'select', // Explicitly set type
            options: firmsList.map(f => ({
              value: f.id,
              label: f.firm_short_name,
            })),
          };
        }
        if (field.name === 'contract_subject_id') {
          return {
            ...field,
            options: contractSubjectsList.map(cs => ({
              value: cs.id,
              label: cs.name,
            })),
          };
        }
        if (field.name === 'scope_of_work_id') {
          return {
            ...field,
            options: scopeOfWorkList.map(sw => ({
              value: sw.id,
              label: sw.name,
            })),
          };
        }
        if (field.name === 'proposal_status_id') {
          return {
            ...field,
            options: proposalStatusesList.map(ps => ({
              value: ps.id,
              label: ps.name,
            })),
          };
        }
        if (field.name === 'sectorIds') {
          return {
            ...field,
            options: sectorsList.map(s => ({
              value: s.id,
              label: s.sector_name || s.name || 'N/A',
            })),
          };
        }
        return field;
      })
      .filter(field => (field.name === 'companyId' ? isRootUser : true));
  }, []);

  const customFilterConfigs = useCallback((filters, data, isRootUser) => {
    return filters.filter(f => (f.showIfRoot ? isRootUser : true));
  }, []);

  const transformEditData = useCallback(offerData => {
    if (!offerData) return {};

    return {
      ...offerData,
      companyId: offerData.company_id,
      firmId: offerData.firm_id,
      firm_established: offerData.firm_established ? 'yes' : 'no',
      firm_short_name: offerData.firm_id,
      proposal_given: offerData.proposal_given ? 'yes' : 'no',
      revised_proposal_given: offerData.revised_proposal_given ? 'yes' : 'no',
      sectorIds: offerData.sectorIds || [],
      proposal_date: parseDateForForm(offerData.proposal_date),
      reminder_date: parseDateForForm(offerData.reminder_date),
      comment: offerData.comment,
      reminder_note: offerData.reminder_note,
      // PDF alanları
      proposal_file: offerData.proposal_file_key
        ? {
            fileName: offerData.proposal_file_key.split('/').pop(),
            key: offerData.proposal_file_key,
            url: `https://ai-thinks-hr-docs.s3.eu-north-1.amazonaws.com/${offerData.proposal_file_key}`,
            size: offerData.proposal_file_size || 0,
            type: 'application/pdf',
          }
        : null,
      revised_proposals: (offerData.revised_proposals || []).map(proposal => ({
        id: proposal.id || Date.now(),
        date: parseDateForForm(proposal.date),
        file: proposal.file || null,
        comment: proposal.comment || '',
      })),
    };
  }, []);

  return (
    <DynamicCrudPage
      entityType="offers"
      screen={SCREENS.OFFERS}
      hook={useOffers}
      additionalHooks={{
        companies: useCompanies,
        firms: useFirms,
        contractSubjects: useContractSubjects,
        scopeOfWork: useScopeOfWork,
        proposalStatuses: useProposalStatuses,
        sectors: useSectors,
      }}
      addButtonTextKey="table.actions.addOffer"
      addButtonIcon={<AddIcon />}
      entityTypeForDataTable="offer"
      customFormFields={customFormFields}
      customFilterConfigs={customFilterConfigs}
      customTransformEditData={transformEditData}
      showAddButton={offersPermissions.canCreate}
      commentEntityType="offer"
    />
  );
};

export default OffersPage;
